'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'

import { handleNewsletterConsentChange } from 'modules/newsletter'
import { getQueryClient } from 'shared/api'

import { updateCustomerDetails, UpdateCustomerValuesPayload } from '../api'
import { useQueryUser } from '../queries/useQueryCustomer'
import {
  extractCustomerFromClientSession,
  extractTokenFromClientSession} from '../session'
import { CustomerTypes } from '../types'

export const useCustomerData = () => {
  const session = useSession()
  const [mergedUser, setMergedUser] = useState<CustomerTypes | undefined>(
    undefined
  )

  const { extractedUser, token } = useMemo(() => {
    return {
      extractedUser: extractCustomerFromClientSession(session),
      token: extractTokenFromClientSession(session)
    }
  }, [session])

  const { data: updatedUser } = useQueryUser(token || '')

  useEffect(() => {
    if (updatedUser) {
      setMergedUser({
        ...extractedUser,
        ...updatedUser
      })
    }
  }, [updatedUser, extractedUser])

  const user = useMemo(
    () => mergedUser || extractedUser,
    [mergedUser, extractedUser]
  )

  const update = useCallback(
    async (values: UpdateCustomerValuesPayload) => {
      let updated: Partial<CustomerTypes> = {}

      if ('isNewsletterSubscriber' in values) {
        updated = {
          ...updated,
          isNewsletterSubscriber: values.isNewsletterSubscriber
        }
        await handleNewsletterConsentChange({
          value: values.isNewsletterSubscriber || false,
          email: user?.email || ''
        })
      }

      const keys = Object.keys(values).filter(
        key => key !== 'isNewsletterSubscriber'
      )

      if (keys.length > 0) {
        const response = await updateCustomerDetails({
          token,
          values
        })

        updated = { ...updated, ...response.data.data }
      }

      await getQueryClient().cancelQueries({ queryKey: ['authUser', token] })

      getQueryClient().setQueryData(['authUser', token], oldData => {
        return oldData
          ? {
              ...oldData,
              ...updated
            }
          : updated
      })

      return updated
    },
    [user, token]
  )

  return {
    user: mergedUser || extractedUser,
    updatedUser,
    token: session.data?.user.token,
    update
  }
}
